import { forwardRef, useState } from "react";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactElement } from "react";
import SVG from "react-inlinesvg";
import IconButton from "src/components/IconButton";
import { cn } from "src/lib/cn";
import { MenuPopoverItem, SudoMenuButton } from "src/sudokit/components/SudoMenuButton";
import { Spinner } from "src/sudokit/components/Spinner";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";

// IconButton has no forwardRef, so SudoMenuButton's cloned ref/onClick go on this wrapping div instead.
export const ImageMenuTrigger = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { ariaLabel: string; }>(({ ariaLabel, ...rest }, ref) => (
  <div
    ref={ref}
    {...rest}
  >
    <IconButton
      size={40}
      iconSize={20}
      smartFill
      src="/assets/three-dots.svg"
      ariaLabel={ariaLabel}
    />
  </div>
));
ImageMenuTrigger.displayName = "ImageMenuTrigger";

// Not IconButton: its inner 32px wrapper squashes when the button is smaller than that.
export const TileMenuTrigger = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>((props, ref) => (
  <button
    ref={ref}
    type="button"
    {...props}
    className="flex size-6 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-full border border-solid border-white/30 bg-black/40 p-0 backdrop-blur-sm transition-colors hover:bg-black/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
  >
    <SVG
      src="/assets/three-dots.svg"
      width={16}
      height={16}
      className="[&_path]:fill-white"
      aria-hidden="true"
    />
  </button>
));
TileMenuTrigger.displayName = "TileMenuTrigger";

export function SetThumbnailMenuItem({ busy, disabled, isThumbnail = false, onSelect }: { busy: boolean; disabled: boolean; isThumbnail?: boolean; onSelect: () => void }) {
  return (
    <MenuPopoverItem
      disabled={disabled || isThumbnail}
      closeOnSelect={false}
      onSelect={onSelect}
      className={cn("gap-2 whitespace-nowrap", disabled && !isThumbnail && "opacity-[0.65]", isThumbnail && "pointer-events-none opacity-40")}
    >
      {busy ? (
        <Spinner
          size="sm"
          className="m-0"
        />
      ) : (
        <SVG
          src={isThumbnail ? "/assets/star.fill.svg" : "/assets/star.svg"}
          className="size-4 shrink-0"
        />
      )}
      Set thumbnail
    </MenuPopoverItem>
  );
}

export const REMOVE_FROM_GALLERY_LABEL = "Remove from gallery";

export const RemoveImageTrigger = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { tooltipDisabled?: boolean; disabled?: boolean }>(({ tooltipDisabled, disabled, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(disabled && "pointer-events-none", className)}
    {...rest}
    onClickCapture={disabled ? (event) => event.stopPropagation() : rest.onClickCapture}
  >
    <IconButton
      size={40}
      iconSize={20}
      smartFill
      src="/assets/delete.svg"
      ariaLabel={REMOVE_FROM_GALLERY_LABEL}
      hoverTipText={REMOVE_FROM_GALLERY_LABEL}
      disableTooltip={tooltipDisabled}
      disabled={disabled}
      // IconButton swallows Enter/Space before they reach the Radix trigger.
      onClick={(event) => {
        if (event.type === "keydown") (event.currentTarget as HTMLElement).closest<HTMLElement>('[data-slot="menu-popover-trigger"]')?.click();
      }}
      wrapperClassName="[&_path]:!fill-app-danger-color"
    />
  </div>
));
RemoveImageTrigger.displayName = "RemoveImageTrigger";

interface RemoveImageMenuProps {
  trigger: ReactElement;
  removing: boolean;
  onRemove: () => void;
  onOpenChange?: (open: boolean) => void;
}

export function RemoveImageMenu({ trigger, removing, onRemove, onOpenChange }: RemoveImageMenuProps) {
  return (
    <SudoMenuButton
      trigger={trigger}
      onOpenChange={onOpenChange}
      side="bottom"
      align="end"
      sideOffset={4}
      menuAriaLabel={REMOVE_FROM_GALLERY_LABEL}
      modal={false}
      menuPortalClassName="z-[1000000000]"
    >
      <MenuPopoverItem
        variant="destructive"
        closeOnSelect={false}
        disabled={removing}
        onSelect={onRemove}
        className="whitespace-nowrap"
      >
        {removing ? "Removing…" : "Remove from gallery"}
      </MenuPopoverItem>
    </SudoMenuButton>
  );
}

const RemoveTileTrigger = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { open: boolean }>(({ open, ...props }, ref) => (
  <SudoTooltip
    text={REMOVE_FROM_GALLERY_LABEL}
    position="top"
    disabled={open}
  >
    <button
      ref={ref}
      type="button"
      aria-label={REMOVE_FROM_GALLERY_LABEL}
      {...props}
      className={cn(
        "use-component-styles flex size-6 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-full border border-solid border-white/30 bg-black/50 p-0 opacity-0 backdrop-blur-sm transition-[opacity,background-color] duration-200 hover:bg-black/70 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white group-hover:opacity-100 group-focus-within:opacity-100",
        open && "opacity-100",
      )}
    >
      <SVG
        src="/assets/delete.svg"
        width={16}
        height={16}
        className="[&_path]:fill-white"
        aria-hidden="true"
      />
    </button>
  </SudoTooltip>
));
RemoveTileTrigger.displayName = "RemoveTileTrigger";

/** Parent tile must be `group relative`. */
export function RemoveImageButton({ onRemove, className }: { onRemove: () => Promise<void>; className?: string }) {
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove();
    } catch {
      // Parent already reports the failure (Sentry + toast); nothing further to do here.
    } finally {
      setRemoving(false);
    }
  };

  return (
    // Portaled menu clicks bubble through the React tree to clickable ancestors.
    <span
      className={cn("absolute right-1.5 top-1.5 z-10", className)}
      onClick={(event) => event.stopPropagation()}
    >
      <RemoveImageMenu
        trigger={<RemoveTileTrigger open={open} />}
        removing={removing}
        onRemove={() => void handleRemove()}
        onOpenChange={setOpen}
      />
    </span>
  );
}
